import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { GlowLayer } from './glow.ts';

/*
 * The heroes' skills, seen (2026-10-26): the ranger's rain of real arrows and its net, the mage's wall of ice
 * and storm cloud, the barbarian's whirl and leap, shields of light (oath, aegis) and the stars over the
 * stunned. Like holy.ts and gearfx.ts: textures are drawn once on canvases, `begin()`/`end()` wrap one sim
 * tick of placing what lasts (a rain, a wall, a cloud, a net, a daze) and `frame()` animates on real time.
 * Short bursts (a volley, a whirl, a dome, a crater) are spawned from battle events.
 * What hits the ground (an arrow, a shard breaking) is queued in `impacts` for the world to dress in dust.
 */

const canvas = (w: number, h = w): [HTMLCanvasElement, CanvasRenderingContext2D] => {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d') as CanvasRenderingContext2D];
};

const tex = (c: HTMLCanvasElement): THREE.Texture => {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
};

const TAU = Math.PI * 2;

/** Every texture and shape made on first use below: a fight makes them all before it starts (warmSkillFx). */
const LAZY: (() => unknown)[] = [];
const once = <T>(make: () => T): (() => T) => {
  let v: T | null = null;
  const get = (): T => (v ??= make());
  LAZY.push(get);
  return get;
};

/**
 * Makes the skills' effects' textures now. Each is painted pixel by pixel the first time it is used; that first
 * time used to be in the middle of a fight, 170 ms on a slow phone when a whirl and a rare blade went off together.
 */
export const warmSkillFx = (): void => {
  for (const f of LAZY) f();
};

/** The ranger's mark on the ground: a ring with four notches, a dashed inner ring and a cross of ticks. */
const reticleTex = once(() => {
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  g.strokeStyle = '#fff';
  g.lineCap = 'round';
  g.shadowColor = '#fff';
  g.shadowBlur = 8;
  g.lineWidth = 7;
  g.beginPath();
  g.arc(C, C, 0.94 * C, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = 3;
  g.setLineDash([18, 22]);
  g.beginPath();
  g.arc(C, C, 0.78 * C, 0, Math.PI * 2);
  g.stroke();
  g.setLineDash([]);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const ca = Math.cos(a), sa = Math.sin(a);
    g.lineWidth = 9;
    g.beginPath();
    g.moveTo(C + ca * 0.98 * C, C + sa * 0.98 * C);
    g.lineTo(C + ca * 0.7 * C, C + sa * 0.7 * C);
    g.stroke();
    // Chevrons pointing in.
    const b = a + Math.PI / 4;
    const cb = Math.cos(b), sb = Math.sin(b);
    const px = -sb, py = cb;
    g.lineWidth = 5;
    g.beginPath();
    g.moveTo(C + cb * 0.6 * C + px * 0.06 * C, C + sb * 0.6 * C + py * 0.06 * C);
    g.lineTo(C + cb * 0.52 * C, C + sb * 0.52 * C);
    g.lineTo(C + cb * 0.6 * C - px * 0.06 * C, C + sb * 0.6 * C - py * 0.06 * C);
    g.stroke();
  }
  g.lineWidth = 3;
  g.beginPath();
  g.arc(C, C, 0.08 * C, 0, Math.PI * 2);
  g.stroke();
  return tex(c);
});

/** A knotted net, round, its cords thicker at the knots and fraying at the rim. */
const netTex = once(() => {
  const S = 256, C = S / 2;
  const [c, g] = canvas(S);
  g.save();
  g.beginPath();
  g.arc(C, C, C - 4, 0, Math.PI * 2);
  g.clip();
  g.strokeStyle = '#fff';
  g.lineWidth = 5;
  const step = 30;
  for (let d = -S; d < 2 * S; d += step) {
    g.beginPath();
    g.moveTo(d, 0);
    g.lineTo(d + S, S);
    g.stroke();
    g.beginPath();
    g.moveTo(d, S);
    g.lineTo(d + S, 0);
    g.stroke();
  }
  g.fillStyle = '#fff';
  for (let y = 0; y < S; y += step / 2) for (let x = (y / (step / 2)) % 2 ? step / 2 : 0; x < S; x += step) {
    g.beginPath();
    g.arc(x, y, 5, 0, Math.PI * 2);
    g.fill();
  }
  g.restore();
  g.strokeStyle = '#fff';
  g.lineWidth = 9;
  g.beginPath();
  g.arc(C, C, C - 7, 0, Math.PI * 2);
  g.stroke();
  return tex(c);
});

/** A soft puff of cloud, lumpy (a few blobs), white on clear. */
const puffTex = once(() => {
  const S = 256;
  const [c, g] = canvas(S);
  let k = 7;
  const r = (): number => {
    k = (Math.imul(k ^ (k >>> 13), 1274126177) + 0x6d2b79f5) >>> 0;
    return k / 4294967296;
  };
  for (let i = 0; i < 9; i++) {
    const a = r() * Math.PI * 2, d = r() * 0.22 * S;
    const x = S / 2 + Math.cos(a) * d, y = S / 2 + Math.sin(a) * d * 0.7;
    const rr = (0.18 + r() * 0.14) * S;
    const gr = g.createRadialGradient(x, y, 0, x, y, rr);
    gr.addColorStop(0, 'rgba(255,255,255,0.55)');
    gr.addColorStop(0.6, 'rgba(255,255,255,0.3)');
    gr.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = gr;
    g.fillRect(0, 0, S, S);
  }
  return tex(c);
});

/** A round glow, for flashes inside the cloud. */
const glowTex = once(() => {
  const S = 128;
  const [c, g] = canvas(S);
  const gr = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  gr.addColorStop(0, 'rgba(255,255,255,1)');
  gr.addColorStop(0.35, 'rgba(255,255,255,0.45)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr;
  g.fillRect(0, 0, S, S);
  return tex(c);
});

/**
 * A gust of wind off the bow, seen from above: a fan opening toward +u (±55°), brightest along its outer edge and
 * fading at both ends, crossed by a few thin arcs of moving air.
 */
const gustTex = once(() => {
  const S = 256, C = S / 2;
  const [c, g] = canvas(S);
  const img = g.createImageData(S, S);
  const span = (55 / 180) * Math.PI;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const dx = (x - C) / C, dy = (y - C) / C;
      const r = Math.hypot(dx, dy);
      if (r > 1 || r < 0.3) continue;
      const a = Math.abs(Math.atan2(dy, dx));
      if (a > span) continue;
      const ends = Math.pow(Math.cos((a / span) * (Math.PI / 2)), 1.4);
      const band = r > 0.94 ? (1 - r) / 0.06 : Math.pow((r - 0.3) / 0.64, 2.6);
      // Thin arcs of air inside the fan.
      const arcs = Math.max(0, 1 - Math.abs(((r * 9) % 1) - 0.5) * 9) * 0.35 * (r < 0.9 ? 1 : 0);
      const o = (y * S + x) * 4;
      img.data[o] = img.data[o + 1] = img.data[o + 2] = 255;
      img.data[o + 3] = Math.round(Math.min(1, (band + arcs * band * 2) * ends) * 255);
    }
  }
  g.putImageData(img, 0, 0);
  return tex(c);
});

/**
 * A streak of light: a thin open five-sided tube from its head (y = 0, brightest) back to its tail (y = -1,
 * nothing), its brightness in its vertex colours (drawn additive, dark is see-through).
 */
const trailGeo = once(() => {
  const g = new THREE.CylinderGeometry(0.35, 1, 1, 5, 4, true).translate(0, -0.5, 0);
  const pos = g.getAttribute('position');
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const k = Math.pow(1 + pos.getY(i), 1.6);
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = k;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
});

/** A four-pointed star with a bright heart (the stunned see these). */
const starTex = once(() => {
  const S = 128, C = S / 2;
  const [c, g] = canvas(S);
  g.fillStyle = '#fff';
  g.shadowColor = '#fff';
  g.shadowBlur = 10;
  g.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 ? 0.16 * C : 0.82 * C;
    const x = C + Math.cos(a) * r, y = C + Math.sin(a) * r;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fill();
  return tex(c);
});

/**
 * A whirl's blade-trail seen from above: a full ring whose brightness runs from hot at angle 0 down to
 * nothing a turn later, its outer edge sharp and its inner edge soft.
 */
const swirlTex = once(() => {
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  const img = g.createImageData(S, S);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const dx = (x - C) / C, dy = (y - C) / C;
      const r = Math.hypot(dx, dy);
      if (r > 1) continue;
      let a = Math.atan2(dy, dx);
      if (a < 0) a += Math.PI * 2;
      const tail = Math.pow(1 - a / (Math.PI * 2), 2.2);
      const band = r < 0.55 ? 0 : r > 0.96 ? Math.max(0, (1 - r) / 0.04) : Math.pow((r - 0.55) / 0.41, 2.4);
      const o = (y * S + x) * 4;
      img.data[o] = img.data[o + 1] = img.data[o + 2] = 255;
      img.data[o + 3] = Math.round(Math.min(1, tail * band * 1.4) * 255);
    }
  }
  g.putImageData(img, 0, 0);
  return tex(c);
});

/** Cracks from a heavy landing: jagged lines out from the centre, branching, dark on clear. */
const crackTex = once(() => {
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  let k = 11;
  const r = (): number => {
    k = (Math.imul(k ^ (k >>> 13), 1274126177) + 0x6d2b79f5) >>> 0;
    return k / 4294967296;
  };
  const gr = g.createRadialGradient(C, C, 0, C, C, 0.42 * C);
  gr.addColorStop(0, 'rgba(255,255,255,0.75)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr;
  g.fillRect(0, 0, S, S);
  g.strokeStyle = '#fff';
  g.lineCap = 'round';
  const crack = (x: number, y: number, a: number, len: number, w: number, depth: number): void => {
    let px = x, py = y;
    const n = 6;
    for (let i = 0; i < n; i++) {
      a += (r() - 0.5) * 0.9;
      const nx = px + Math.cos(a) * (len / n), ny = py + Math.sin(a) * (len / n);
      g.lineWidth = w * (1 - i / n) + 1;
      g.beginPath();
      g.moveTo(px, py);
      g.lineTo(nx, ny);
      g.stroke();
      if (depth > 0 && r() < 0.35) crack(nx, ny, a + (r() < 0.5 ? -1 : 1) * (0.5 + r() * 0.5), len * 0.45, w * 0.6, depth - 1);
      px = nx;
      py = ny;
    }
  };
  for (let i = 0; i < 9; i++) crack(C, C, (i / 9) * Math.PI * 2 + r() * 0.4, (0.55 + r() * 0.4) * C, 7, 2);
  return tex(c);
});

/** A rim-lit shell (fresnel), with bands that climb: oath and aegis domes. */
const DOME_VERT = /* glsl */ `
varying vec3 vN;
varying vec3 vV;
varying float vY;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vN = normalize(normalMatrix * normal);
  vV = normalize(-mv.xyz);
  vY = position.y;
  gl_Position = projectionMatrix * mv;
}`;

const DOME_FRAG = /* glsl */ `
uniform vec3 color;
uniform float opacity;
uniform float time;
varying vec3 vN;
varying vec3 vV;
varying float vY;
void main() {
  float rim = pow(1.0 - abs(dot(normalize(vN), normalize(vV))), 2.0);
  float band = 0.5 + 0.5 * sin(vY * 14.0 - time * 9.0);
  float foot = smoothstep(0.0, 0.12, vY);
  float a = (0.05 + rim * (0.65 + 0.35 * band)) * opacity * foot;
  gl_FragColor = vec4(color * (0.85 + 0.35 * band), a);
  #include <colorspace_fragment>
}`;

/** A flat additive mark, front side only (pulled toward the camera like the other floor marks). */
const flatMat = (map: THREE.Texture, color: THREE.ColorRepresentation, opacity = 0): THREE.MeshBasicMaterial =>
  new THREE.MeshBasicMaterial({ map, color, transparent: true, opacity, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });

const domeMaterial = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    name: 'dome',
    uniforms: { color: { value: new THREE.Color() }, opacity: { value: 0 }, time: { value: 0 } },
    vertexShader: DOME_VERT,
    fragmentShader: DOME_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide, forceSinglePass: true,
  });

/** Arrow: shaft along +y with the head on top, dark wood, steel head, pale fletching with a red cock feather. */
const arrowGeo = once(() => {
  const paint = (geo: THREE.BufferGeometry, hex: string): THREE.BufferGeometry => {
    const col = new THREE.Color(hex);
    const n = geo.getAttribute('position').count;
    const a = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) col.toArray(a, i * 3);
    geo.setAttribute('color', new THREE.BufferAttribute(a, 3));
    return geo.index ? geo.toNonIndexed() : geo;
  };
  const shaft = paint(new THREE.CylinderGeometry(0.016, 0.016, 0.7, 5).translate(0, 0.35, 0), '#5a4634');
  const head = paint(new THREE.ConeGeometry(0.045, 0.13, 5).translate(0, 0.76, 0), '#c3cad2');
  const f1 = paint(new THREE.PlaneGeometry(0.08, 0.16).translate(0.04, 0.1, 0), '#e9edf2');
  const f2 = paint(new THREE.PlaneGeometry(0.08, 0.16).translate(0.04, 0.1, 0).rotateY((Math.PI * 2) / 3), '#ff4f3a');
  const f3 = paint(new THREE.PlaneGeometry(0.08, 0.16).translate(0.04, 0.1, 0).rotateY((Math.PI * 4) / 3), '#e9edf2');
  const g = mergeGeometries([shaft, head, f1, f2, f3], false) as THREE.BufferGeometry;
  g.computeVertexNormals();
  return g;
});

/** An ice shard: a five-sided spike, flat-shaded, base at y = 0 and tip at y = 1. */
const shardGeo = once(() => {
  const g = new THREE.CylinderGeometry(0, 0.17, 1, 5, 1).translate(0, 0.5, 0).toNonIndexed();
  g.computeVertexNormals();
  return g;
});

interface Arrow {
  /** Where it starts falling, where it lands, and the direction it flies (unit). */
  from: THREE.Vector3;
  to: THREE.Vector3;
  t0: number;
  fall: number;
  stick: number;
  /** Rising, at the volley (no landing, no dust). */
  up: boolean;
  hit?: boolean;
  /** Size (1 = a plain arrow; the titan's arrow is far bigger). */
  s: number;
  /** A glint at its head while it flies (null: none). */
  glint: THREE.Color | null;
}

/** A short flash of light (an arrow biting the ground, a rope snapping taut). */
interface Flash {
  x: number;
  y: number;
  z: number;
  t0: number;
  life: number;
  size: number;
  color: THREE.Color;
}

/** Glints at the heads of falling arrows, and the flash where one bites. */
const GLINT = new THREE.Color('#eaf2ff');
const BITE = new THREE.Color('#f4f7ff');
const ROPE = new THREE.Color('#dfe9ff');
const FLASHES = 48;
/** Streaks drawn per frame at most (arrows in flight, the warrior's tethers). */
const TRAILS = 160;

interface Rain {
  id: number;
  p: THREE.Vector3;
  r: number;
  mark: THREE.Mesh;
  want: number;
  shown: number;
  carry: number;
  seen: boolean;
}

interface Wall {
  id: number;
  shards: { p: THREE.Vector3; h: number; tilt: THREE.Quaternion; w: number; delay: number }[];
  t0: number;
  /** Time it broke (0 while standing). */
  broke: number;
  seen: boolean;
}

interface Cloud {
  id: number;
  p: THREE.Vector3;
  r: number;
  g: THREE.Group;
  puffs: THREE.Sprite[];
  flash: THREE.Sprite;
  shade: THREE.Mesh;
  flashAt: number;
  want: number;
  shown: number;
  seen: boolean;
}

interface Burst {
  mesh: THREE.Object3D;
  t0: number;
  life: number;
  step: (k: number, now: number) => void;
  done: () => void;
}

interface Tag {
  g: THREE.Object3D;
  seen: boolean;
  /** A net in flight: how far it has opened (0 folded, 1 spread). */
  open?: number;
}

/*
 * RETIRE: a finished effect's materials are left to the garbage collector, never disposed. Disposing the last
 * material of a kind let three free its shader program, so the next effect of that kind compiled it again: two
 * programs every few seconds of a wave, 300-600 ms each on a phone (client-perf, 2026-10-29). Not disposed, the
 * program stays; the material holds nothing on the GPU of its own.
 */
export class SkillFxLayer {
  readonly group = new THREE.Group();
  /** Ground hits since the world last read them: arrows landing, a wall shattering. */
  readonly impacts: { p: THREE.Vector3; kind: 'arrow' | 'ice' }[] = [];
  quality: 'high' | 'low' = 'high';
  private now = 0;
  private arrows: Arrow[] = [];
  private arrowMesh: THREE.InstancedMesh;
  private streakMesh: THREE.InstancedMesh;
  private shardMesh: THREE.InstancedMesh;
  private rains = new Map<number, Rain>();
  private walls = new Map<number, Wall>();
  private clouds = new Map<number, Cloud>();
  private nets = new Map<number, Tag>();
  private drapes = new Map<number, Tag>();
  private dazes = new Map<number, Tag & { stars: THREE.Sprite[] }>();
  private bursts: Burst[] = [];
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private sc = new THREE.Vector3();
  private up = new THREE.Vector3(0, 1, 0);
  private lit = new THREE.Color('#a8b8e0');
  private domeGeo = new THREE.SphereGeometry(1, 32, 14, 0, Math.PI * 2, 0, Math.PI / 2);
  /** Dome materials whose dome faded, to reuse. */
  private domes: THREE.ShaderMaterial[] = [];
  /** Streaks placed this frame (see `streak`): one instanced draw. */
  private trailMesh: THREE.InstancedMesh;
  private trailN = 0;
  private tc = new THREE.Color();
  /** Glints and flashes: one instanced draw, refilled every frame. */
  private glow = new GlowLayer(768);
  private flashes: Flash[] = [];
  private flashAt = 0;
  private dir = new THREE.Vector3();
  private w = new THREE.Vector3();
  private qq = new THREE.Quaternion();
  private xAxis = new THREE.Vector3(1, 0, 0);
  private primedNow = false;

  constructor() {
    this.arrowMesh = new THREE.InstancedMesh(arrowGeo(), new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7, metalness: 0.1, side: THREE.DoubleSide }), 600);
    this.arrowMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.arrowMesh.frustumCulled = false;
    this.arrowMesh.count = 0;
    // The streak behind a falling arrow: a thin pale smear along its flight.
    this.streakMesh = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.01, 0.035, 1, 4, 1, true).translate(0, -0.5, 0),
      new THREE.MeshBasicMaterial({ color: '#dfe8f2', transparent: true, opacity: 0.18, depthWrite: false, blending: THREE.AdditiveBlending }),
      600,
    );
    this.streakMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.streakMesh.frustumCulled = false;
    this.streakMesh.count = 0;
    this.shardMesh = new THREE.InstancedMesh(
      shardGeo(),
      new THREE.MeshStandardMaterial({ color: '#a9dcff', emissive: '#2f74b0', emissiveIntensity: 0.55, roughness: 0.15, metalness: 0.15, flatShading: true, transparent: true, opacity: 0.86 }),
      256,
    );
    this.shardMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.shardMesh.frustumCulled = false;
    this.shardMesh.count = 0;
    this.shardMesh.renderOrder = 6;
    this.group.add(this.arrowMesh, this.streakMesh, this.shardMesh);
    // Streaks: arrows in flight and the warrior's tethers, colour per instance (with its buffer from the start, so
    // the program compiled ahead is the one every streak draws with).
    this.trailMesh = new THREE.InstancedMesh(trailGeo(), new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }), TRAILS);
    this.trailMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.trailMesh.setColorAt(0, new THREE.Color(0, 0, 0));
    this.trailMesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.trailMesh.frustumCulled = false;
    this.trailMesh.count = 0;
    this.trailMesh.renderOrder = 8;
    for (let i = 0; i < FLASHES; i++) this.flashes.push({ x: 0, y: 0, z: 0, t0: -9, life: 1, size: 0, color: new THREE.Color() });
    this.group.add(this.trailMesh, this.glow.mesh);
    // Far under the ground until the fight's compile ahead is done (it compiles only what is visible): it keeps
    // the dome's shader in the scene, so the first oath or aegis of a fight doesn't compile it on the spot.
    // The ranger's net and its drape: cut out, so one pass (two-sided transparent took two, a program each, and
    // neither compiled ahead), and kept here likewise.
    const net = new THREE.Mesh(new THREE.CircleGeometry(0.6, 8), new THREE.MeshBasicMaterial({ map: netTex(), color: '#cfc8bb', transparent: true, alphaTest: 0.3, side: THREE.DoubleSide, forceSinglePass: true }));
    // A flat additive mark (gusts, the net's glowing ropes, the rain's mark): front side only, one program for all.
    const mark = new THREE.Mesh(new THREE.CircleGeometry(1, 8), flatMat(glowTex(), '#ffffff'));
    this.keep = [new THREE.Mesh(this.domeGeo, domeMaterial()), net, mark];
    for (const k of this.keep) {
      k.position.set(0, -60, 0);
      k.scale.setScalar(0.0001);
      k.frustumCulled = false;
      this.group.add(k);
    }
  }

  private readonly keep: THREE.Mesh[];
  /** The view's shaders are linked: the keepers can go. */
  set primed(v: boolean) {
    for (const k of this.keep) k.visible = !v;
    this.primedNow = v;
  }

  private get low(): boolean {
    return this.quality === 'low';
  }

  /** Starts a sim tick: whatever lasts and isn't placed again fades out (or breaks). */
  begin(): void {
    this.trailN = 0;
    for (const m of [this.rains, this.walls, this.clouds, this.nets, this.drapes, this.dazes] as Map<number, { seen: boolean }>[]) for (const x of m.values()) x.seen = false;
  }

  end(): void {
    this.trailMesh.count = this.trailN;
    this.trailMesh.instanceMatrix.needsUpdate = true;
    this.trailMesh.instanceColor!.needsUpdate = true;
    this.trailMesh.visible = this.trailN > 0 || !this.primedNow;
    for (const r of this.rains.values()) if (!r.seen) r.want = 0;
    for (const c of this.clouds.values()) if (!c.seen) c.want = 0;
    for (const w of this.walls.values()) {
      if (w.seen || w.broke) continue;
      w.broke = this.now;
      for (const s of w.shards) this.impacts.push({ p: s.p.clone().setY(s.p.y + s.h * 0.4), kind: 'ice' });
    }
    for (const [id, t] of this.nets) if (!t.seen) this.drop(this.nets, id, t);
    for (const [id, t] of this.drapes) if (!t.seen) this.drop(this.drapes, id, t);
    for (const [id, t] of this.dazes) if (!t.seen) this.drop(this.dazes, id, t);
  }

  private drop(map: Map<number, Tag>, id: number, t: Tag): void {
    // Its materials are left to the collector, not disposed (see RETIRE).
    this.group.remove(t.g);
    map.delete(id);
  }

  /* ---------- the ranger ---------- */

  /**
   * A streak of light this frame, its head at (x, y, z) and its tail `len` back against the unit direction
   * (dx, dy, dz) it flies: an arrow's trail, a tether. Placed every frame between `begin()` and `end()`.
   */
  streak(x: number, y: number, z: number, dx: number, dy: number, dz: number, len: number, width: number, color: THREE.Color, alpha: number): void {
    if (this.trailN >= TRAILS || len <= 0.01) return;
    this.dir.set(dx, dy, dz);
    this.q.setFromUnitVectors(this.up, this.dir);
    this.m.compose(this.w.set(x, y, z), this.q, this.sc.set(width, len, width));
    this.trailMesh.setMatrixAt(this.trailN, this.m);
    this.trailMesh.setColorAt(this.trailN, this.tc.copy(color).multiplyScalar(alpha));
    this.trailN++;
  }

  /** A short flash of light at p (an arrow biting, a rope snapping): it swells a little and fades. */
  flash(p: THREE.Vector3, color: THREE.Color, size: number, life = 0.14): void {
    const f = this.flashes[this.flashAt++ % FLASHES] as Flash;
    f.x = p.x;
    f.y = p.y;
    f.z = p.z;
    f.t0 = this.now;
    f.life = life;
    f.size = size;
    f.color.copy(color);
  }

  /** A gust of wind off the bow: a fan of moving air opening along `dir` (flat, at the height of p). */
  gust(p: THREE.Vector3, dir: THREE.Vector3, r: number, color: THREE.ColorRepresentation, life = 0.34): void {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2), flatMat(gustTex(), color));
    m.renderOrder = 12;
    m.position.copy(p);
    m.rotation.y = Math.atan2(-dir.z, dir.x);
    this.spawn(m, life, (k) => {
      const e = 1 - (1 - k) * (1 - k);
      const s = r * (0.35 + 0.65 * e);
      m.scale.set(s, 1, s * (0.8 + 0.2 * e));
      (m.material as THREE.MeshBasicMaterial).opacity = 0.5 * Math.min(1, k * 10) * (1 - k) * (1 - k);
    });
  }

  /** One arrow loosed flat and fast from `from` to `to` over `dur` seconds (a piercing shot, the titan's arrow). */
  shot(from: THREE.Vector3, to: THREE.Vector3, dur: number, size = 1, glint: THREE.Color | null = GLINT): void {
    this.arrows.push({ from: from.clone(), to: to.clone(), t0: this.now, fall: dur, stick: 0, up: false, s: size, glint });
  }

  /** The volley that becomes a rain: arrows loosed steeply upward from the ranger toward the sky over the mark. */
  volley(from: THREE.Vector3, to: THREE.Vector3): void {
    const n = this.low ? 10 : 22;
    for (let i = 0; i < n; i++) {
      const a = from.clone().setY(from.y + 0.9);
      a.x += (Math.random() - 0.5) * 0.3;
      a.z += (Math.random() - 0.5) * 0.3;
      const b = from.clone().lerp(to, 0.35 + Math.random() * 0.2);
      b.y = from.y + 7 + Math.random() * 2;
      b.x += (Math.random() - 0.5) * 2.2;
      b.z += (Math.random() - 0.5) * 2.2;
      this.arrows.push({ from: a, to: b, t0: this.now + i * 0.014, fall: 0.38, stick: 0, up: true, s: 1, glint: GLINT });
    }
    // The bowstring's snap: a ring of air round the ranger and a flash at the bow.
    this.flash(from.clone().setY(from.y + 1), BITE, 1.1, 0.18);
  }

  /** One tick of an arrow rain: the mark on the ground, the shadow of the arrows over it, and they keep falling into it. */
  rain(id: number, p: THREE.Vector3, r: number): void {
    let z = this.rains.get(id);
    if (!z) {
      const mark = new THREE.Mesh(new THREE.CircleGeometry(1, 48), flatMat(reticleTex(), '#ffb8a6'));
      mark.rotation.x = -Math.PI / 2;
      mark.renderOrder = 3;
      // The shadow of the cloud of arrows coming down: a dark pool under the mark.
      const shade = new THREE.Mesh(
        new THREE.CircleGeometry(1, 40),
        new THREE.MeshBasicMaterial({ map: glowTex(), color: '#05070c', transparent: true, opacity: 0, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2 }),
      );
      shade.rotation.x = -Math.PI / 2;
      shade.renderOrder = 2;
      mark.userData.shade = shade;
      this.group.add(shade, mark);
      // The first arrows are the volley coming down: a short hold before they land.
      z = { id, p: p.clone(), r, mark, want: 1, shown: 0, carry: -0.25, seen: true };
      this.rains.set(id, z);
    }
    z.seen = true;
    z.want = 1;
    z.p.copy(p);
    z.r = r;
  }

  /** A net in flight: folded as it leaves the hand, spreading open and spinning as it flies, its ropes glowing. */
  net(id: number, p: THREE.Vector3, open = 1): void {
    let t = this.nets.get(id);
    if (!t) {
      const m = new THREE.Mesh(new THREE.CircleGeometry(0.6, 24), new THREE.MeshBasicMaterial({ map: netTex(), color: '#cfc8bb', transparent: true, alphaTest: 0.3, side: THREE.DoubleSide, forceSinglePass: true }));
      m.rotation.x = -Math.PI / 2 + 0.5;
      // Its ropes, lit: the same weave drawn in cold light over it, so it reads against the dark ground.
      const lit = new THREE.Mesh(new THREE.CircleGeometry(0.62, 24), flatMat(netTex(), '#cfe0ff', 0.55));
      lit.rotation.x = -Math.PI / 2 + 0.5;
      const g = new THREE.Group();
      g.add(m, lit);
      this.group.add(g);
      t = { g, seen: true, open: 0 };
      this.nets.set(id, t);
    }
    t.seen = true;
    t.open = open;
    t.g.position.copy(p);
  }

  /** A nightmare caught in a net: the net draped over it, its ropes glowing, pinned at the rim. */
  netted(id: number, p: THREE.Vector3, h: number): void {
    let t = this.drapes.get(id);
    if (!t) {
      const map = netTex().clone();
      map.wrapS = map.wrapT = THREE.RepeatWrapping;
      map.repeat.set(1.5, 0.8);
      map.needsUpdate = true;
      const cone = new THREE.ConeGeometry(1, 1, 16, 1, true).translate(0, 0.5, 0);
      const m = new THREE.Mesh(cone, new THREE.MeshBasicMaterial({ map, color: '#ddd6ca', transparent: true, alphaTest: 0.3, side: THREE.DoubleSide, forceSinglePass: true }));
      const lit = new THREE.Mesh(cone, flatMat(map, '#bcd2ff', 0.3));
      lit.scale.setScalar(1.03);
      m.add(lit);
      const g = new THREE.Group();
      g.add(m);
      g.userData.t0 = this.now;
      this.group.add(g);
      t = { g, seen: true };
      this.drapes.set(id, t);
      // Pulled taut: the ropes snap with a flash.
      this.flash(this.w.copy(p).setY(p.y + h * 0.6), ROPE, 1.4, 0.2);
    }
    t.seen = true;
    t.g.position.copy(p);
    t.g.userData.h = h;
  }

  /* ---------- the mage ---------- */

  /** One tick of a wall of ice: spikes rise out of each hex and stand until the wall melts, then shatter. */
  frost(id: number, cells: THREE.Vector3[]): void {
    let w = this.walls.get(id);
    if (!w) {
      const shards: Wall['shards'] = [];
      const per = this.low ? 2 : 4;
      cells.forEach((c, ci) => {
        for (let i = 0; i < per; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = i === 0 ? 0 : 0.2 + Math.random() * 0.25;
          const p = c.clone().add(this.v.set(Math.cos(a) * d, -0.05, Math.sin(a) * d));
          const tilt = new THREE.Quaternion().setFromEuler(new THREE.Euler((Math.random() - 0.5) * 0.5 + Math.sin(a) * d * 0.9, Math.random() * Math.PI, (Math.random() - 0.5) * 0.5 - Math.cos(a) * d * 0.9));
          shards.push({ p, h: i === 0 ? 1.35 + Math.random() * 0.3 : 0.6 + Math.random() * 0.55, tilt, w: i === 0 ? 1.5 : 0.8 + Math.random() * 0.5, delay: ci * 0.05 + i * 0.03 });
        }
      });
      w = { id, shards, t0: this.now, broke: 0, seen: true };
      this.walls.set(id, w);
    }
    w.seen = true;
  }

  /** One tick of a storm: a dark cloud over the circle that lights from within at each bolt. */
  storm(id: number, p: THREE.Vector3, r: number): void {
    let c = this.clouds.get(id);
    if (!c) {
      const g = new THREE.Group();
      const puffs: THREE.Sprite[] = [];
      const n = this.low ? 6 : 11;
      for (let i = 0; i < n; i++) {
        const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: puffTex(), color: '#4d566e', transparent: true, opacity: 0, depthWrite: false, fog: false, toneMapped: false }));
        const a = (i / n) * Math.PI * 2;
        s.userData.a = a;
        s.userData.d = i === 0 ? 0 : 0.35 + ((i * 37) % 10) / 16;
        s.userData.y = ((i * 53) % 10) / 20;
        s.userData.s = 1.6 + ((i * 29) % 10) / 9;
        s.renderOrder = 14;
        g.add(s);
        puffs.push(s);
      }
      const flash = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex(), color: '#bcd6ff', transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
      flash.renderOrder = 15;
      g.add(flash);
      const shade = new THREE.Mesh(new THREE.CircleGeometry(1, 40), new THREE.MeshBasicMaterial({ map: glowTex(), color: '#0a0d16', transparent: true, opacity: 0, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2 }));
      shade.rotation.x = -Math.PI / 2;
      shade.renderOrder = 2;
      this.group.add(g, shade);
      c = { id, p: p.clone(), r, g, puffs, flash, shade, flashAt: -9, want: 1, shown: 0, seen: true };
      this.clouds.set(id, c);
    }
    c.seen = true;
    c.want = 1;
    c.p.copy(p);
    c.r = r;
  }

  /** Where a storm's bolts start (under its cloud), and the cloud lights. */
  stormBolt(id: number): THREE.Vector3 | null {
    const c = this.clouds.get(id);
    if (!c) return null;
    c.flashAt = this.now;
    return c.p.clone().setY(c.p.y + 3.2);
  }

  /* ---------- blows and shields ---------- */

  /** A whirl: a blade-trail spinning twice round the barbarian, low, then a second fainter one. */
  whirl(p: THREE.Vector3, r: number): void {
    for (const [k, delay, y, col] of [[0, 0, 0.55, '#ffe6dc'], [1, 0.06, 0.35, '#ff6a4a']] as const) {
      const m = new THREE.Mesh(new THREE.CircleGeometry(1, 48), new THREE.MeshBasicMaterial({ map: swirlTex(), color: col, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, forceSinglePass: true }));
      m.rotation.x = -Math.PI / 2;
      m.renderOrder = 12;
      const g = new THREE.Group();
      g.add(m);
      g.position.set(p.x, p.y + y, p.z);
      g.rotation.x = k ? -0.12 : 0.1;
      this.spawn(g, 0.42, (t) => {
        const kk = Math.max(0, t - delay / 0.42);
        m.rotation.z = -kk * Math.PI * 4.2;
        const s = r * (0.75 + 0.3 * Math.sqrt(kk));
        g.scale.set(s, s, s);
        (m.material as THREE.MeshBasicMaterial).opacity = (k ? 0.7 : 1) * Math.sin(Math.min(1, kk) * Math.PI);
      });
    }
  }

  /**
   * A warrior's cleave: a short flat sweep of steel-white light across the target at chest height, a cold rim
   * to it, `r` wide, swinging the way the blow came (`yaw`). A few at once at most: a warrior swings often.
   */
  cleave(p: THREE.Vector3, r: number, yaw: number): void {
    if (this.cleaves >= (this.low ? 2 : 4)) return;
    this.cleaves++;
    const m = new THREE.Mesh(new THREE.CircleGeometry(1, 40), flatMat(swirlTex(), '#e6eef8'));
    m.rotation.x = -Math.PI / 2;
    m.renderOrder = 12;
    const g = new THREE.Group();
    g.add(m);
    g.position.copy(p);
    g.rotation.y = yaw;
    g.scale.setScalar(r);
    const mat = m.material as THREE.MeshBasicMaterial;
    this.spawn(g, 0.26, (k) => {
      // Sweeps about a third of a turn, easing out, flaring and fading.
      m.rotation.z = -(1 - (1 - k) * (1 - k)) * 2.2;
      const s = r * (0.85 + 0.15 * k);
      g.scale.set(s, s, s);
      mat.opacity = 0.95 * Math.min(1, k * 8) * (1 - k);
    });
    // Counted out when it ends (spawn's done runs once).
    const b = this.bursts[this.bursts.length - 1] as Burst;
    const done = b.done;
    b.done = () => {
      this.cleaves--;
      done();
    };
  }

  private cleaves = 0;

  /** A leap: a streak arcing from where it jumped to where it lands. */
  leap(from: THREE.Vector3, to: THREE.Vector3): void {
    const n = this.low ? 8 : 16;
    const lift = Math.min(3, 1 + from.distanceTo(to) * 0.35);
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const q = from.clone().lerp(to, t);
      q.y += 0.7 + Math.sin(t * Math.PI) * lift;
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex(), color: i > n * 0.7 ? '#ffd2c4' : '#c9b8a8', transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
      s.position.copy(q);
      s.renderOrder = 12;
      this.spawn(s, 0.5, (k) => {
        const kk = k * 0.5 * 1.8 - t * 0.35;
        const a = kk < 0 ? 0 : Math.max(0, 1 - kk * 1.6);
        (s.material as THREE.SpriteMaterial).opacity = 0.7 * a;
        const sz = 0.55 * (0.6 + t * 0.6) * (0.6 + a * 0.5);
        s.scale.set(sz, sz, 1);
      });
    }
  }

  /** Where something heavy came down: cracks in the ground that fade. */
  crater(p: THREE.Vector3, r: number, hot = false): void {
    const m = new THREE.Mesh(new THREE.CircleGeometry(1, 32), new THREE.MeshBasicMaterial({ map: crackTex(), color: hot ? '#ff5a2a' : '#120e0c', transparent: true, opacity: 0, depthWrite: false, blending: hot ? THREE.AdditiveBlending : THREE.NormalBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 }));
    m.rotation.x = -Math.PI / 2;
    m.rotation.z = Math.random() * Math.PI * 2;
    m.position.set(p.x, p.y + 0.03, p.z);
    m.renderOrder = 2;
    this.spawn(m, hot ? 1.2 : 2.6, (k) => {
      const s = r * (0.85 + 0.15 * Math.min(1, k * 12));
      m.scale.set(s, s, s);
      (m.material as THREE.MeshBasicMaterial).opacity = (hot ? 0.9 : 0.6) * Math.min(1, k * 20) * (1 - k * k);
    });
  }

  /** A shell of light: it swells up past its size, settles, and fades (the oath, the aegis). */
  dome(p: THREE.Vector3, r: number, color: string, life = 0.9): void {
    // Reused, not disposed: freeing the last one let three drop the program, so every oath compiled it again
    // (a stall of most of a second under ANGLE on Windows).
    const mat = this.domes.pop() ?? domeMaterial();
    mat.userData.pool = this.domes;
    mat.uniforms.color!.value.set(color);
    const m = new THREE.Mesh(this.domeGeo, mat);
    m.position.copy(p);
    m.renderOrder = 11;
    this.spawn(m, life, (k, now) => {
      // Ease out with a little overshoot.
      const e = 1 + 2.2 * Math.pow(k * 2.2 - 1, 3) + 1.2 * Math.pow(k * 2.2 - 1, 2);
      const s = r * (k < 1 / 2.2 ? Math.max(0.05, e) : 1);
      m.scale.set(s, s * 0.85, s);
      mat.uniforms.opacity!.value = 1.4 * Math.min(1, k * 8) * (1 - Math.pow(k, 3));
      mat.uniforms.time!.value = now;
    });
  }

  /* ---------- the stunned ---------- */

  /** Stars circling over a stunned head. */
  dazed(id: number, p: THREE.Vector3, top: number): void {
    let t = this.dazes.get(id);
    if (!t) {
      const g = new THREE.Group();
      const stars: THREE.Sprite[] = [];
      for (let i = 0; i < 3; i++) {
        const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: starTex(), color: i === 1 ? '#ffffff' : '#ffe9a8', transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
        s.renderOrder = 14;
        g.add(s);
        stars.push(s);
      }
      this.group.add(g);
      t = { g, seen: true, stars };
      this.dazes.set(id, t);
    }
    t.seen = true;
    t.g.position.set(p.x, p.y + top + 0.12, p.z);
  }

  /* ---------- plumbing ---------- */

  private spawn(mesh: THREE.Object3D, life: number, step: Burst['step']): void {
    this.group.add(mesh);
    step(0, this.now);
    this.bursts.push({
      mesh,
      t0: this.now,
      life,
      step,
      done: () => {
        this.group.remove(mesh);
        mesh.traverse((o) => {
          const mm = (o as THREE.Mesh).material as THREE.Material | undefined;
          const pool = mm?.userData.pool as THREE.Material[] | undefined;
          // Not disposed (see RETIRE).
          if (pool) pool.push(mm as THREE.Material);
          const geo = (o as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
          if (geo && geo !== this.domeGeo) geo.dispose();
        });
      },
    });
  }

  /** Every animation frame. */
  frame(now: number): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.glow.begin();
    this.rainFrame(now, dt);
    this.arrowFrame(now);
    this.wallFrame(now);
    this.cloudFrame(now);
    for (const t of this.nets.values()) {
      // Folded as it leaves the hand, it opens to its full spread half way, spinning, its rim breathing.
      const o = t.open ?? 1;
      const e = Math.min(1, o * 2);
      const s = (0.35 + 0.65 * e * e * (3 - 2 * e)) * (0.9 + 0.1 * Math.sin(now * 14));
      for (const c of t.g.children) {
        c.rotation.z = now * 9;
        c.scale.set(s, s, s);
      }
      const lit = t.g.children[1] as THREE.Mesh;
      (lit.material as THREE.MeshBasicMaterial).opacity = 0.35 + 0.25 * e;
      const gp = t.g.position;
      this.glow.add(gp.x, gp.y, gp.z, ROPE, 0.9 * s + 0.3, 0.22);
    }
    for (const t of this.drapes.values()) {
      const h = (t.g.userData.h as number) ?? 1;
      const age = now - (t.g.userData.t0 as number);
      const k = Math.min(1, age / 0.18);
      // Drops on from above, then sways a little; the lit ropes breathe.
      const r = (0.62 + 0.06 * Math.sin(now * 3)) * (1.5 - 0.5 * k);
      const d = t.g.children[0] as THREE.Mesh;
      d.scale.set(r, (h + 0.15) * (1.6 - 0.6 * k), r);
      d.position.y = (1 - k) * 0.8;
      d.rotation.y = Math.sin(now * 2) * 0.15;
      const lit = d.children[0] as THREE.Mesh;
      (lit.material as THREE.MeshBasicMaterial).opacity = 0.18 + 0.12 * Math.sin(now * 4) + 0.5 * Math.max(0, 1 - age / 0.4);
      // Pinned to the ground at four points round the rim.
      const gp = t.g.position;
      if (k >= 1) {
        for (let i = 0; i < 4; i++) {
          const a = (i / 4) * TAU + 0.4;
          this.glow.add(gp.x + Math.cos(a) * r * 0.95, gp.y + 0.05, gp.z + Math.sin(a) * r * 0.95, ROPE, 0.22, 0.55);
        }
      }
    }
    for (const t of this.dazes.values()) {
      t.stars.forEach((s, i) => {
        const a = now * 4.2 + (i / 3) * Math.PI * 2;
        s.position.set(Math.cos(a) * 0.32, Math.sin(a * 2) * 0.04, Math.sin(a) * 0.32);
        const z = 0.24 + 0.06 * Math.sin(now * 9 + i);
        s.scale.set(z, z, 1);
        (s.material as THREE.SpriteMaterial).rotation = now * 2 + i;
      });
    }
    const keep: Burst[] = [];
    for (const b of this.bursts) {
      const k = (now - b.t0) / b.life;
      if (k >= 1) {
        b.done();
        continue;
      }
      b.step(Math.max(0, k), now);
      keep.push(b);
    }
    this.bursts = keep;
    for (const f of this.flashes) {
      const k = (now - f.t0) / f.life;
      if (k < 0 || k >= 1) continue;
      this.glow.add(f.x, f.y, f.z, f.color, f.size * (0.6 + 0.4 * k), 1 - k * k);
    }
    this.glow.end();
  }

  private rainFrame(now: number, dt: number): void {
    for (const [id, z] of this.rains) {
      z.shown += (z.want - z.shown) * 0.15;
      const mm = z.mark.material as THREE.MeshBasicMaterial;
      mm.opacity = 0.55 * z.shown * (0.85 + 0.15 * Math.sin(now * 5));
      z.mark.position.set(z.p.x, z.p.y + 0.04, z.p.z);
      const s = z.r * (1.08 - 0.08 * z.shown);
      z.mark.scale.set(s, s, s);
      z.mark.rotation.z = now * 0.35;
      const shade = z.mark.userData.shade as THREE.Mesh;
      shade.position.set(z.p.x, z.p.y + 0.03, z.p.z);
      shade.scale.setScalar(z.r * 1.3);
      (shade.material as THREE.MeshBasicMaterial).opacity = 0.55 * z.shown;
      if (z.want === 0 && z.shown < 0.02) {
        // The materials are not disposed (see RETIRE).
        this.group.remove(z.mark, shade);
        z.mark.geometry.dispose();
        shade.geometry.dispose();
        this.rains.delete(id);
        continue;
      }
      if (z.want === 0) continue;
      // Arrows per second, more for a wider circle.
      z.carry += dt * (this.low ? 15 : 38) * Math.min(2, (z.r * z.r) / 6);
      while (z.carry >= 1) {
        z.carry -= 1;
        const a = Math.random() * Math.PI * 2;
        const d = Math.sqrt(Math.random()) * z.r * 0.95;
        const to = z.p.clone().add(this.v.set(Math.cos(a) * d, 0, Math.sin(a) * d));
        // All from the same side of the sky, a little scattered, at a steep slant.
        const from = to.clone().add(this.v.set(-1.6 + (Math.random() - 0.5) * 0.5, 7.5, -1 + (Math.random() - 0.5) * 0.5));
        // One in three glints as it comes down (a whistle of steel in the dark).
        this.arrows.push({ from, to, t0: now - Math.random() * 0.05, fall: 0.26 + Math.random() * 0.06, stick: 1.1 + Math.random() * 0.5, up: false, s: 1, glint: Math.random() < 0.34 ? GLINT : null });
      }
    }
  }

  private arrowFrame(now: number): void {
    let n = 0;
    let ns = 0;
    const keep: Arrow[] = [];
    const dir = this.dir;
    for (const a of this.arrows) {
      const t = (now - a.t0) / a.fall;
      if (t < 0) {
        keep.push(a);
        continue;
      }
      if (t >= 1 && a.up) continue;
      if (t >= 1 && !a.stick) continue;
      dir.copy(a.to).sub(a.from).normalize();
      if (t < 1) {
        // In flight: accelerating, head first, a pale streak behind (a flat shot flies straight at full speed).
        const e = a.up ? 1 - (1 - t) * (1 - t) : a.stick ? t * t * 0.4 + t * 0.6 : t;
        const p = this.v.copy(a.from).lerp(a.to, e);
        this.q.setFromUnitVectors(this.up, dir);
        this.m.compose(p, this.q, this.sc.set(a.s, a.s, a.s));
        if (n < 600) this.arrowMesh.setMatrixAt(n++, this.m);
        if (ns < 600) {
          this.m.compose(this.w.copy(p).addScaledVector(dir, 0.75 * a.s), this.q, this.sc.set(a.s, (a.up ? 1.2 : a.stick ? 1.3 : 2.6) * a.s, a.s));
          this.streakMesh.setMatrixAt(ns++, this.m);
        }
        if (a.glint) {
          // A glint of steel at its head.
          this.w.copy(p).addScaledVector(dir, 0.8 * a.s);
          this.glow.add(this.w.x, this.w.y, this.w.z, a.glint, 0.06 + 0.05 * a.s, 0.6);
        }
        keep.push(a);
        continue;
      }
      // Landed: once, a puff and a bite of light; then it stands in the ground, sinking away at the end.
      if (!a.hit) {
        a.hit = true;
        this.impacts.push({ p: a.to.clone(), kind: 'arrow' });
        if (Math.random() < (this.low ? 0.15 : 0.3)) this.flash(this.w.copy(a.to).setY(a.to.y + 0.06), BITE, 0.2, 0.1);
      }
      const st = (now - a.t0 - a.fall) / a.stick;
      if (st >= 1) continue;
      const sink = st > 0.75 ? (st - 0.75) / 0.25 : 0;
      const quiver = st < 0.08 ? Math.sin(st * 160) * 0.06 * (1 - st / 0.08) : 0;
      this.q.setFromUnitVectors(this.up, dir);
      this.q.multiply(this.qq.setFromAxisAngle(this.xAxis, quiver));
      const p = this.v.copy(a.to).addScaledVector(dir, 0.18 + sink * 0.6 - 0.84);
      this.m.compose(p, this.q, this.sc.set(1, 1, 1));
      if (n < 600) this.arrowMesh.setMatrixAt(n++, this.m);
      keep.push(a);
    }
    this.arrows = keep;
    this.arrowMesh.count = n;
    this.streakMesh.count = ns;
    this.arrowMesh.instanceMatrix.needsUpdate = true;
    this.streakMesh.instanceMatrix.needsUpdate = true;
  }

  private wallFrame(now: number): void {
    let n = 0;
    for (const [id, w] of this.walls) {
      const broken = w.broke ? (now - w.broke) / 0.22 : 0;
      if (broken >= 1) {
        this.walls.delete(id);
        continue;
      }
      for (const s of w.shards) {
        const t = Math.max(0, Math.min(1, (now - w.t0 - s.delay) / 0.22));
        // Out of the ground with a little overshoot.
        const rise = t < 1 ? 1 - Math.pow(1 - t, 3) + Math.sin(t * Math.PI) * 0.18 : 1;
        const h = s.h * rise * (1 - broken);
        if (h <= 0.01) continue;
        const wid = s.w * (1 - broken * 0.5);
        this.m.compose(s.p, s.tilt, this.sc.set(wid, h, wid));
        if (n < 256) this.shardMesh.setMatrixAt(n++, this.m);
      }
    }
    this.shardMesh.count = n;
    this.shardMesh.instanceMatrix.needsUpdate = true;
    const mat = this.shardMesh.material as THREE.MeshStandardMaterial;
    mat.emissiveIntensity = 0.5 + 0.12 * Math.sin(now * 2.4);
  }

  private cloudFrame(now: number): void {
    for (const [id, c] of this.clouds) {
      c.shown += (c.want - c.shown) * 0.08;
      if (c.want === 0 && c.shown < 0.02) {
        // The materials are not disposed (see RETIRE).
        this.group.remove(c.g, c.shade);
        c.shade.geometry.dispose();
        this.clouds.delete(id);
        continue;
      }
      const fl = Math.max(0, 1 - (now - c.flashAt) / 0.12);
      c.g.position.set(c.p.x, c.p.y + 3.4, c.p.z);
      for (const s of c.puffs) {
        const a = (s.userData.a as number) + now * 0.25;
        const d = (s.userData.d as number) * c.r * 0.85;
        s.position.set(Math.cos(a) * d, (s.userData.y as number) + Math.sin(now * 0.8 + a) * 0.08, Math.sin(a) * d);
        const sz = (s.userData.s as number) * (0.55 + 0.45 * c.shown) * Math.max(1, c.r * 0.55);
        s.scale.set(sz, sz * 0.62, 1);
        const sm = s.material;
        sm.opacity = 0.85 * c.shown;
        sm.color.set('#4d566e').lerp(this.lit, fl);
      }
      c.flash.position.set(0, -0.1, 0);
      const fs = c.r * 1.6;
      c.flash.scale.set(fs, fs * 0.6, 1);
      c.flash.material.opacity = fl * 0.45 * c.shown;
      c.shade.position.set(c.p.x, c.p.y + 0.03, c.p.z);
      c.shade.scale.setScalar(c.r * 1.35);
      (c.shade.material as THREE.MeshBasicMaterial).opacity = 0.7 * c.shown;
    }
  }

  dispose(): void {
    for (const b of this.bursts) b.done();
    this.bursts = [];
  }
}
